import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import { Button } from '@/components/ui/button';
import { Text } from '@/components/ui/text';
import { Icon } from '~/global/components/icon/Icon';
import { logger } from '@/lib/logger';
import { usePowerSync } from '~/powersync/contexts/power-sync/PowerSync';
import { db } from '@/lib/local-api';
import { useMutation } from '@tanstack/react-query';
import { useModal } from '@/contexts/ModalContext';
import { SportActivitySelectorModal } from '../sport-activity-selector-modal/SportActivitySelectorModal';
import type { SportActivityRecord } from '~/powersync/generated/schema';

export interface WorkoutFormActionsProps {
  /**
   * Whether the form is currently in edit mode
   */
  isEditMode?: boolean;
  /**
   * Whether the form is currently loading/submitting
   */
  isLoading?: boolean;
  /**
   * The workout ID to add supersets to
   */
  workoutId: string;
}

export const WorkoutFormActions = ({
  isEditMode = false,
  isLoading = false,
  workoutId,
}: WorkoutFormActionsProps) => {
  const { t } = useTranslation('workout');
  const powerSync = usePowerSync();
  const { showModal } = useModal();

  // Clean local-api mutation to add a new superset
  const addSupersetMutation = useMutation({
    mutationFn: () => db.Workout.mutations.createSuperset(powerSync, workoutId),
  });

  // Clean local-api mutation to add a new superset with exercise
  const addExerciseMutation = useMutation({
    mutationFn: (exercise: SportActivityRecord) =>
      db.Workout.mutations.createSupersetWithExercise(
        powerSync,
        workoutId,
        exercise,
      ),
  });

  const handleAddExercise = () => {
    logger.debug('Add Exercise clicked for workout:', workoutId);

    showModal(SportActivitySelectorModal, {
      onSportActivitySelected: (exercise: SportActivityRecord) => {
        logger.debug('Exercise selected:', exercise.name);
        addExerciseMutation.mutate(exercise);
      },
    });
  };

  const handleAddSuperset = () => {
    logger.debug('Add Superset clicked for workout:', workoutId);
    addSupersetMutation.mutate();
  };

  // Only show these buttons in edit mode
  if (!isEditMode) {
    return null;
  }

  return (
    <View className="flex-col md:flex-row gap-3 items-start">
      <Button
        testID="workout-add-exercise-button"
        variant="surface"
        onPress={handleAddExercise}
        disabled={isLoading || addExerciseMutation.isPending}
        className="flex-row items-center gap-2 md:w-auto w-auto"
      >
        <Icon name="Plus" size={16} className="text-text-secondary" />
        <Text>{t('form.buttons.addExercise')}</Text>
      </Button>

      <Button
        testID="workout-add-superset-button"
        variant="surface"
        onPress={handleAddSuperset}
        disabled={isLoading}
        className="flex-row items-center gap-2 md:w-auto w-auto"
      >
        <Icon name="Plus" size={16} className="text-text-secondary" />
        <Text>{t('form.buttons.addSuperset')}</Text>
      </Button>
    </View>
  );
};
